import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const images = [
  "https://cdn0.weddingwire.in/article/1678/3_2/1280/jpg/8761-wedding-background-images-infinitememories-lead.webp",
  "https://images.pexels.com/photos/1456613/pexels-photo-1456613.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1",
  "https://images.pexels.com/photos/1444442/pexels-photo-1444442.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1",
  "https://www.fourthforce.in/blog/wp-content/uploads/2017/04/426358-Kycb.jpg",
  "https://images.unsplash.com/photo-1603584397340-7718e53a552e?fm=jpg&q=60&w=3000",
  "https://images.pexels.com/photos/248077/pexels-photo-248077.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1",
  "https://images.pexels.com/photos/265856/pexels-photo-265856.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1",
];

const LanguageSelect = () => {
  const { t, i18n } = useTranslation();
  const [currentImage, setCurrentImage] = useState(0);
  const [hoveredButton, setHoveredButton] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % images.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const handleLanguageSelect = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem("language", lang);

    // Dispatch custom event to notify all components
    window.dispatchEvent(
      new CustomEvent("languageChanged", {
        detail: { language: lang },
      })
    );

    navigate("/howitswork");
  };

  useEffect(() => {
    // Initialize language from localStorage or current i18n language
    const storedLanguage = localStorage.getItem("language");
    const currentI18nLanguage = i18n.language;

    let languageToUse;
    if (storedLanguage && ["en", "ta"].includes(storedLanguage)) {
      languageToUse = storedLanguage;
    } else if (
      currentI18nLanguage &&
      ["en", "ta"].includes(currentI18nLanguage)
    ) {
      languageToUse = currentI18nLanguage;
    } else {
      languageToUse = "en";
    }

    // Only change language if it's different from current
    if (i18n.language !== languageToUse) {
      i18n.changeLanguage(languageToUse);
    }

    // Store the language if not already stored
    if (!storedLanguage) {
      localStorage.setItem("language", languageToUse);
    }
  }, [i18n]);

  // Listen for language changes from other components
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (
        e.key === "language" &&
        e.newValue &&
        ["en", "ta"].includes(e.newValue)
      ) {
        if (i18n.language !== e.newValue) {
          i18n.changeLanguage(e.newValue);
        }
      }
    };

    const handleCustomLanguageChange = (e) => {
      if (
        e.detail &&
        e.detail.language &&
        ["en", "ta"].includes(e.detail.language)
      ) {
        if (i18n.language !== e.detail.language) {
          i18n.changeLanguage(e.detail.language);
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("languageChanged", handleCustomLanguageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("languageChanged", handleCustomLanguageChange);
    };
  }, [i18n]);

  return (
    <div className="relative flex flex-col items-center justify-center min-h-screen w-full overflow-hidden bg-gray-900">
      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-32 h-32 bg-purple-500/20 rounded-full blur-xl" />
      <div className="absolute bottom-0 right-0 w-32 h-32 bg-blue-500/20 rounded-full blur-xl" />

      <AnimatePresence>
        {images.map((img, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, scale: 1.2 }}
            animate={{
              opacity: index === currentImage ? 1 : 0,
              scale: index === currentImage ? 1 : 1.2,
              rotate: index === currentImage ? 0 : 5,
            }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 1.8, ease: "easeInOut" }}
            className="absolute inset-0 w-full h-full bg-cover bg-center"
            style={{ backgroundImage: `url(${img})` }}
          >
            <motion.div
              className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/50 to-black/80"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 1 }}
            />
          </motion.div>
        ))}
      </AnimatePresence>

      <div className="relative z-10 w-full max-w-4xl mx-auto px-4 py-8">
        <motion.div
          className="flex flex-col items-center space-y-12"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, type: "spring", stiffness: 100 }}
        >
          <motion.div
            className="text-center space-y-6"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <motion.h1
              className="text-4xl md:text-5xl font-bold text-white"
              animate={{
                backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
              }}
              style={{
                backgroundSize: "200% auto",
                backgroundImage:
                  "linear-gradient(to right, #ffffff, #a855f7, #ffffff)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
              transition={{ duration: 5, repeat: Infinity, ease: "linear" }}
            >
              {t("welcome")}
            </motion.h1>
            <motion.p
              className="text-gray-300 text-xl md:text-2xl font-light"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.8 }}
            >
              {t("languageSelectSubtitle")}
            </motion.p>
          </motion.div>

          {/* Language Buttons */}
          <div className="flex flex-col md:flex-row gap-8 w-full max-w-2xl mx-auto">
            {[
              { lang: "en", label: t("languages.english") },
              { lang: "ta", label: t("languages.tamil") },
            ].map((item) => (
              <motion.button
                key={item.lang}
                className="group relative w-full md:w-1/2 p-6 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white/20 transition-all duration-300 overflow-hidden"
                onClick={() => handleLanguageSelect(item.lang)}
                onHoverStart={() => setHoveredButton(item.lang)}
                onHoverEnd={() => setHoveredButton(null)}
                whileHover={{ scale: 1.05, y: -5 }}
                whileTap={{ scale: 0.98 }}
                initial={{ x: item.lang === "en" ? -50 : 50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ type: "spring", stiffness: 300, delay: 0.2 }}
              >
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-purple-500/30 via-pink-500/30 to-purple-500/30"
                  initial={{ x: "-100%" }}
                  animate={{
                    x: hoveredButton === item.lang ? "100%" : "-100%",
                  }}
                  transition={{ duration: 0.4 }}
                />
                <div className="relative z-10 flex items-center justify-between">
                  <motion.span
                    className="text-white text-2xl font-semibold"
                    animate={{
                      scale: hoveredButton === item.lang ? 1.1 : 1,
                    }}
                    transition={{ duration: 0.15 }}
                  >
                    {item.label}
                  </motion.span>
                  <motion.div
                    animate={{
                      x: hoveredButton === item.lang ? 10 : 0,
                      opacity: hoveredButton === item.lang ? 1 : 0,
                    }}
                    transition={{
                      type: "spring",
                      stiffness: 400,
                      duration: 0.15,
                    }}
                  >
                    <ArrowRight className="w-6 h-6 text-white" />
                  </motion.div>
                </div>
              </motion.button>
            ))}
          </div>

          {/* Enhanced Indicators */}
          <motion.div
            className="flex gap-3 mt-12"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.8, duration: 0.6 }}
          >
            {images.map((_, index) => (
              <motion.button
                key={index}
                onClick={() => setCurrentImage(index)}
                className={`relative h-2 rounded-full overflow-hidden ${
                  index === currentImage ? "w-8" : "w-2"
                } bg-white/30`}
                animate={{
                  scale: index === currentImage ? 1.2 : 1,
                }}
                whileHover={{ scale: 1.5 }}
                transition={{ duration: 0.2 }}
              >
                {index === currentImage && (
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-500"
                    initial={{ x: "-100%" }}
                    animate={{ x: "0%" }}
                    transition={{ duration: 1.5 }}
                  />
                )}
              </motion.button>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default LanguageSelect;
